import React from 'react';
import { useState, useEffect, useFormContext, Hooks, Code, Form, Field, Name, Title, Info, Actions, Toast, API, Utility, Button, Files, Control, Messages, OptionType } from 'helpers/FormImports';
import { MediaList, MediaUpload, useLogoImage } from 'helpers/ImageImports';
import { Location } from 'models/LocationModel';
import { LocationData } from 'models/BaseModel';
import HtmlEditor from 'components/common/HtmlEditor';

const LocationForm: React.FC = () => {
  const width = { view: 20, open: 20, modify: 20 };
  const [formId, formName] = [Code.Form.Id.LOCATION, Code.Form.Name.LOCATION];
  const {keyId, activeId, setActiveId, loading, setLoading, message, setMessage, showMenu, setShowMenu, confirm, setConfirm, error, setError, menuPosition, setMenuPosition, contextMenuRef, iframeRef, formRef } = useFormContext();
  const [activeTab, setActiveTab] = useState<string>(Utility.Storage.GetTab(formName));
  const [cityOptions, setCityOptions] = Hooks.Form.Options();
  const [formData, setFormData] = useState<Location>({});
  const [fileList, setFileList] = useState<MediaList>({});
  const [printData, setPrintData] = useState<string>('');
  const [settingData, setSettingData] = useState<LocationData>({});
  const [showSetting, setShowSetting] = useState<boolean>(false);

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const {logoImage, handleLogoImageChange, handleAttachLogoImage, handleDeleteLogoImage } = useLogoImage(fileList, setFileList, setLogoUrl, setError);
 
  Form.Click.Outside([contextMenuRef],[() => setShowMenu(false),() => {}],true);
  const Privilege = Hooks.Privilege.List(formId);
  Hooks.Privilege.HasOpen(Privilege);
  
  useEffect(() => {
    Files.Json.API(Code.File.CITY, Code.Option.Common.SELECT, setCityOptions, setError);
    
    API.Data.GetByKeyId<Location>(formName, keyId, setLoading, setError)
      .then(async(data) => { if(data) {
        setActiveId(data.location_active); 
        Files.List.ParseFile(data.file_list, setFileList);
        const tempSettingData = JSON.parse(data.location_data || "{}");
        setSettingData(tempSettingData);
        const formData = await Form.Map.Data(data);
        setPrintData(data.location_print || '');
        setFormData((prevData) => ({
          ...prevData,
          ...formData,
        }));
      }})
      setLoading(false);
  }, []);

  const onSubmit = (e: React.FormEvent) => {
    if (!Toast.Validate.Form(e,setMessage)) return;
    const finalData = Utility.Sanitize.FormData({
      ...formData,
      location_print: printData,
      company_id: Utility.Storage.CompanyId(),
      user_id: Utility.Storage.UserId(),
      location_phone: Utility.Convert.Number(formData.location_phone),
      file_list: JSON.stringify(Files.List.Clean(fileList)),
      location_data: Utility.Clean.JsonObject(settingData),
    });

    API.Data.Save(formName, keyId, finalData, setMessage)

  };

  useEffect(() => {
    if (formData && formData.location_address) {
      setShowSetting(formData.location_address.includes("settings"));
    }
  }, [formData.location_address]);

  return (
    <Control.Form.Frame setMenuPosition={setMenuPosition} setShowMenu={setShowMenu}>
      <Messages.Toast Messages={message} Error={error} />
      <Messages.Confirm Messages={confirm} onConfirm={() => API.Data.Delete(formName, keyId, activeId, setMessage)} onRefuse={()=>{}} />
      {!loading && (
        <form id={Code.Form.Name.EDIT} ref={formRef} onSubmit={onSubmit} autoComplete="off">
          <Button.Save keyId={keyId} activeId={activeId} handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} formName={formName} formId={formId} Privilege={Privilege} />
          <Control.Tab.Header titles={showSetting ? ["Details", "Images", "Settings"] : ["Details", "Images"]} activeTab={activeTab} formName={formName} setActiveTab={setActiveTab} />
          <Control.Form.Data>
 
            <Control.Tab.Content id={Code.Tab.Tab1} isActive={activeTab === Code.Tab.Tab1}>
              <Control.Row>
                <Field.Text id={Name.Location.Name} value={formData.location_name} title={"Location Name"} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
                <Field.Text id={Name.Location.FacilityNumber} value={formData.location_facility_number} title={"Facility Number"} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true} />
              </Control.Row>
              <Control.Row>
                <Field.Text id={Name.Location.Phone} value={Utility.Format.Phone(formData.location_phone)} title={Title.Phone} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[0,50,1]} require={true} />
                <Field.Text id={Name.Location.Email} value={formData.location_email} title={Title.Email} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} require={true} />
              </Control.Row>
              <Control.Box>
                <Field.Text id={Name.Location.Website} value={formData.location_website} title={Title.Website} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
                <Field.Area id={Name.Location.Address} value={formData.location_address} title={Title.Address} onChange={(e) => Form.Field.Change(e, formData, setFormData)} />
              </Control.Box>
              <Control.Row>
                <Field.Select id={Name.City.Id} value={formData.city_id} title={Title.City} options={cityOptions}  onChange={(e) => Form.Field.Change(e, formData, setFormData)}  size={[0,50,1]} require={true} />
                <Field.Text id={Name.Location.Postal} value={formData.location_postal} title={Title.Postal} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
              <Control.Box>
                <Field.Title title='Printable' />
                <HtmlEditor content={printData} setContent={setPrintData} height={260}/>
              </Control.Box>
              <Control.Row>
                <Field.Color id={Name.Location.Color} value={formData.location_color} title={Title.Color} onChange={(e) => Form.Field.Change(e, formData, setFormData)} size={[1,50,0]} />
              </Control.Row>
            </Control.Tab.Content>

            <Control.Tab.Content id={Code.Tab.Tab2} isActive={activeTab === Code.Tab.Tab2}>
              <MediaUpload label={Title.Logo} imageUrl={logoUrl} handleImageChange={handleLogoImageChange} handleAttachImage={handleAttachLogoImage} handleDeleteImage={handleDeleteLogoImage} summary={Info.LogoSize} />
            </Control.Tab.Content>

          
            <Control.Tab.Content id={Code.Tab.Tab3} isActive={activeTab === Code.Tab.Tab3}>
              <Control.Container>
                <Field.ContainerHeader title='Account Numbers' />
                <Control.Row>
                  <Field.Text id={Code.Setting.Location.WalmartEER} title='Walmart' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.WalmartEER as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[0,50,2]} />
                  <Field.Text id={Code.Setting.Location.DoctorOptical} title='Doctor Optical' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.DoctorOptical as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[2,50,0]} />
                </Control.Row>
              </Control.Container>

              <Control.Container>
                <Field.ContainerHeader title='Email Settings' />
                <Control.Row>
                  <Field.Text id={Code.Setting.Location.SMTPHost} title='Host' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.SMTPHost as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[0,50,2]} />
                  <Field.Text id={Code.Setting.Location.SMTPPort} title='Port' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.SMTPPort as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[2,30,2]} />
                  <Field.Text id={Code.Setting.Location.SMTPSsl} title='SSL' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.SMTPSsl as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[2,20,0]} disabled={true} />
                </Control.Row>
                <Control.Row>
                  <Field.Text id={Code.Setting.Location.SMTPUserName} title='User Name' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.SMTPUserName as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[0,50,2]} />
                  <Field.HiddenPassword id={Code.Setting.Location.SMTPPassword} title='Password' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.SMTPPassword as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[2,50,0]} />
                </Control.Row>
                <Control.Row>
                  <Field.Text id={Code.Setting.Location.SMTPSenderEmail} title='Sender Email' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.SMTPSenderEmail as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[0,50,2]} />
                  <Field.Text id={Code.Setting.Location.SMTPSenderTitle} title='Sender Title' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.SMTPSenderTitle as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[2,50,0]} />
                </Control.Row>
              </Control.Container>

              <Control.Container>
                <Field.ContainerHeader title='Fax Settings' />
                <Control.Row>
                  <Field.Text id={Code.Setting.Location.FaxHost} title='Host' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.FaxHost as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[0,50,2]} />
                  <Field.Text id={Code.Setting.Location.FaxPort} title='Port' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.FaxPort as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[2,30,2]} />
                  <Field.Text id={Code.Setting.Location.FaxSsl} title='SSL' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.FaxSsl as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[2,20,0]} disabled={true} />
                </Control.Row>
                <Control.Row>
                  <Field.Text id={Code.Setting.Location.FaxUserName} title='User Name' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.FaxUserName as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[0,50,2]} />
                  <Field.HiddenPassword id={Code.Setting.Location.FaxPassword} title='Password' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.FaxPassword as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[2,50,0]} />
                </Control.Row>
                <Control.Row>
                  <Field.Text id={Code.Setting.Location.FaxSenderEmail} title='Sender Email' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.FaxSenderEmail as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[0,50,2]} />
                  <Field.Text id={Code.Setting.Location.FaxSenderTitle} title='Sender Title' value={Utility.Get.FieldValue(settingData, Code.Setting.Location.FaxSenderTitle as keyof LocationData)} onChange={(e) => Form.MultiValue.Change(e, settingData, setSettingData)} size={[2,50,0]} />
                </Control.Row>
              </Control.Container>

            </Control.Tab.Content>

          </Control.Form.Data>
        </form>
      )}
      {showMenu && (<div ref={contextMenuRef}><Control.FormContextMenu keyId={keyId} activeId={activeId} showMenu={showMenu} formName={formName}  handleDelete={() => Toast.Delete.CurrentRecord(activeId, setConfirm)} setShowMenu={setShowMenu} Privilege={Privilege} pos={menuPosition} /></div>)}
      <Actions.Form />
    </Control.Form.Frame>
  );
};

export default LocationForm;